import { ThemeOptions } from '@wix/da-react-context/pkg/constants';
import { PapiDeviation, PapiProfileskin } from '@wix/da-papi-types';
import { BackgroundImageDisplayMode } from '@wix/da-shared-react/pkg/Background/types';

export const profileSkinsStreamId = 'profile_skins';

const CACHE_BUSTER = 3;

/**
 * We accept URLs or css rules for an image through the profile skin admin tool. To improve the admin tool experience,
 * we allow raw URLs instead of ones wrapped in `url()`. So we need to wrap them manually here.
 *
 * This is hacky, I know.
 */
export function wrapPapiBackgroundUrl(url: string) {
  return url.startsWith('https') ? `url(${url}?${CACHE_BUSTER})` : url;
}

/**
 * Converts a backend skin type to what we're accustomed to working with on the frontend.
 */
export function papiSkinToFedSkin(papiSkin: PapiProfileskin): ProfileSkin {
  return {
    skinId: `${papiSkin.skinId}`,
    skinName: papiSkin.title,
    backgroundPreview: wrapPapiBackgroundUrl(papiSkin.previewImage),
    skin: {
      baseTheme: papiSkin.baseTheme as ThemeOptions,
      background: papiSkin.backgroundImage
        ? wrapPapiBackgroundUrl(papiSkin.backgroundImage)
        : '',
      staticBackground: papiSkin.staticBackgroundImage
        ? wrapPapiBackgroundUrl(papiSkin.staticBackgroundImage)
        : undefined,
    },
    // All skins are core only, no current plans to open this up to free users
    isCore: true,
  };
}

export interface ProfileSkinsSlice {
  activeSkin?: ProfileSkin;
  skinSelectorShown: boolean;
  isSkinSaving: boolean;

  /**
   * The user can only have one custom skin. This is the first profile skin returned by the initial list fetch.
   */
  customSkin?: ProfileSkin;
}

export interface ProfileSkin {
  skinId: string;
  skinName: string;
  backgroundPreview: string;
  featureProtected?: string;
  skin?: {
    baseTheme: ThemeOptions;
    background: string;
    staticBackground?: string;
    imageUrl?: string;
    image?: PapiDeviation;
    imageDisplayMode?: BackgroundImageDisplayMode;
  };
  isCore: boolean;
}

export const officialSkin: ProfileSkin = {
  skinId: 'official',
  skinName: 'Official',
  isCore: false,
  backgroundPreview: 'linear-gradient(225deg, #5d5d6c 0%, #22272b 100%)',
};

export const customSkin: ProfileSkin = {
  skinId: 'custom',
  skinName: 'Custom',
  isCore: true,
  backgroundPreview: `var(--g-bg-secondary)`,
};

export const holidaySkins: ProfileSkin[] = [
  {
    skinId: 'spiderweb',
    skinName: 'Spider Web',
    isCore: true,
    featureProtected: 'holiday_skins',
    backgroundPreview: `url(https://st.deviantart.net/eclipse/profile-skins/spiderweb_preview.jpeg?${CACHE_BUSTER})`,
    skin: {
      baseTheme: ThemeOptions.DARK,
      background: `url(https://st.deviantart.net/eclipse/profile-skins/spiderweb.png?${CACHE_BUSTER})`,
    },
  },
  {
    skinId: 'leaves',
    skinName: 'Orange Leaves',
    isCore: true,
    featureProtected: 'holiday_skins',
    backgroundPreview: `url(https://st.deviantart.net/eclipse/profile-skins/leaves_preview.jpeg?${CACHE_BUSTER})`,
    skin: {
      baseTheme: ThemeOptions.DARK,
      background: `url(https://st.deviantart.net/eclipse/profile-skins/leaves.gif?${CACHE_BUSTER})`,
      staticBackground: `url(https://st.deviantart.net/eclipse/profile-skins/leaves_static.gif?${CACHE_BUSTER})`,
    },
  },
  {
    skinId: 'pumpkin_faces',
    skinName: 'Pumpkin Faces',
    isCore: true,
    featureProtected: 'holiday_skins',
    backgroundPreview: `url(https://st.deviantart.net/eclipse/profile-skins/pumpkin_faces_preview.jpeg?${CACHE_BUSTER})`,
    skin: {
      baseTheme: ThemeOptions.DARK,
      background: `url(https://st.deviantart.net/eclipse/profile-skins/pumpkin_faces.gif?${CACHE_BUSTER})`,
      staticBackground: `url(https://st.deviantart.net/eclipse/profile-skins/pumpkin_faces_static.gif?${CACHE_BUSTER})`,
    },
  },
  {
    skinId: 'rotating_skulls',
    skinName: 'Rotating Skulls',
    isCore: true,
    featureProtected: 'holiday_skins',
    backgroundPreview: `url(https://st.deviantart.net/eclipse/profile-skins/rotating_skulls_preview.jpeg?${CACHE_BUSTER})`,
    skin: {
      baseTheme: ThemeOptions.DARK,
      background: `url(https://st.deviantart.net/eclipse/profile-skins/rotating_skulls.gif?${CACHE_BUSTER})`,
      staticBackground: `url(https://st.deviantart.net/eclipse/profile-skins/rotating_skulls_static.gif?${CACHE_BUSTER})`,
    },
  },
  {
    skinId: 'snakes',
    skinName: 'Snakes',
    isCore: true,
    featureProtected: 'holiday_skins',
    backgroundPreview: `url(https://st.deviantart.net/eclipse/profile-skins/snakes_preview.jpeg?${CACHE_BUSTER})`,
    skin: {
      baseTheme: ThemeOptions.DARK,
      background: `url(https://st.deviantart.net/eclipse/profile-skins/snakes.png?${CACHE_BUSTER})`,
    },
  },
];

export const premiumSkins: ProfileSkin[] = [
  ...holidaySkins,
  {
    skinId: 'animal',
    skinName: 'Animal Print',
    isCore: true,
    backgroundPreview: `url(https://st.deviantart.net/eclipse/profile-skins/animal_preview.png?${CACHE_BUSTER})`,
    skin: {
      baseTheme: ThemeOptions.DARK,
      background: `url(https://st.deviantart.net/eclipse/profile-skins/animal.png?${CACHE_BUSTER})`,
    },
  },
  {
    skinId: 'smiley',
    skinName: 'Smiley Faces',
    isCore: true,
    backgroundPreview: `url(https://st.deviantart.net/eclipse/profile-skins/smiley_preview.png?${CACHE_BUSTER})`,
    skin: {
      baseTheme: ThemeOptions.DARK,
      background: `url(https://st.deviantart.net/eclipse/profile-skins/smiley.png?${CACHE_BUSTER})`,
    },
  },
  {
    skinId: 'stars',
    skinName: 'Star Field',
    isCore: true,
    backgroundPreview: `url(https://st.deviantart.net/eclipse/profile-skins/stars_preview.png?${CACHE_BUSTER})`,
    skin: {
      baseTheme: ThemeOptions.DARK,
      background: `url(https://st.deviantart.net/eclipse/profile-skins/stars.gif?${CACHE_BUSTER})`,
      staticBackground: `url(https://st.deviantart.net/eclipse/profile-skins/stars-static.gif?${CACHE_BUSTER})`,
    },
  },
  {
    skinId: 'hearts',
    skinName: 'Hearts',
    isCore: true,
    backgroundPreview: `url(https://st.deviantart.net/eclipse/profile-skins/hearts_preview.png?${CACHE_BUSTER})`,
    skin: {
      baseTheme: ThemeOptions.LIGHT,
      background: `url(https://st.deviantart.net/eclipse/profile-skins/hearts.gif?${CACHE_BUSTER})`,
      staticBackground: `url(https://st.deviantart.net/eclipse/profile-skins/hearts-static.gif?${CACHE_BUSTER})`,
    },
  },
  {
    skinId: 'yellowboxes',
    skinName: 'Green Boxes',
    isCore: true,
    backgroundPreview: `url(https://st.deviantart.net/eclipse/profile-skins/yellow_box_preview.png?${CACHE_BUSTER})`,
    skin: {
      baseTheme: ThemeOptions.LIGHT,
      background: `url(https://st.deviantart.net/eclipse/profile-skins/yellow_box.png?${CACHE_BUSTER})`,
    },
  },
  {
    skinId: 'greenboxes',
    skinName: 'Green Boxes',
    isCore: true,
    backgroundPreview: `url(https://st.deviantart.net/eclipse/profile-skins/green_box_preview.png?${CACHE_BUSTER})`,
    skin: {
      baseTheme: ThemeOptions.DARK,
      background: `url(https://st.deviantart.net/eclipse/profile-skins/green_box.png?${CACHE_BUSTER})`,
    },
  },
  {
    skinId: 'darkrain',
    skinName: 'Dark Rain',
    isCore: true,
    backgroundPreview: `url(https://st.deviantart.net/eclipse/profile-skins/dark_rain_preview.png?${CACHE_BUSTER})`,
    skin: {
      baseTheme: ThemeOptions.DARK,
      background: `url(https://st.deviantart.net/eclipse/profile-skins/dark_rain.gif?${CACHE_BUSTER})`,
      staticBackground: `url(https://st.deviantart.net/eclipse/profile-skins/dark_rain_static.gif?${CACHE_BUSTER})`,
    },
  },
  {
    skinId: 'skulls',
    skinName: 'Pretty Skulls',
    isCore: true,
    backgroundPreview: `url(https://st.deviantart.net/eclipse/profile-skins/skulls_preview.png?${CACHE_BUSTER})`,
    skin: {
      baseTheme: ThemeOptions.LIGHT,
      background: `url(https://st.deviantart.net/eclipse/profile-skins/skulls.png?${CACHE_BUSTER})`,
    },
  },
  {
    skinId: 'healing',
    skinName: 'Healing',
    isCore: true,
    backgroundPreview: `url(https://st.deviantart.net/eclipse/profile-skins/healing_preview.png?${CACHE_BUSTER})`,
    skin: {
      baseTheme: ThemeOptions.DARK,
      background: `url(https://st.deviantart.net/eclipse/profile-skins/healing.png?${CACHE_BUSTER})`,
    },
  },
  {
    skinId: 'greendots',
    skinName: 'Green Dots',
    isCore: true,
    backgroundPreview: `url(https://st.deviantart.net/eclipse/profile-skins/green_dots_preview.png?${CACHE_BUSTER})`,
    skin: {
      baseTheme: ThemeOptions.DARK,
      background: `url(https://st.deviantart.net/eclipse/profile-skins/green_dots.png?${CACHE_BUSTER})`,
    },
  },
  {
    skinId: 'skullspixel',
    skinName: 'Pixel Skulls',
    isCore: true,
    backgroundPreview: `url(https://st.deviantart.net/eclipse/profile-skins/skulls_pixel_preview.png?${CACHE_BUSTER})`,
    skin: {
      baseTheme: ThemeOptions.DARK,
      background: `url(https://st.deviantart.net/eclipse/profile-skins/skulls_pixel.png?${CACHE_BUSTER})`,
    },
  },
  {
    skinId: 'redzebra',
    skinName: 'Red Zebra',
    isCore: true,
    backgroundPreview: `url(https://st.deviantart.net/eclipse/profile-skins/red_zebra_preview.png?${CACHE_BUSTER})`,
    skin: {
      baseTheme: ThemeOptions.DARK,
      background: `url(https://st.deviantart.net/eclipse/profile-skins/red_zebra.png?${CACHE_BUSTER})`,
    },
  },
  {
    skinId: 'purplespots',
    skinName: 'Purple Spots',
    isCore: true,
    backgroundPreview: `url(https://st.deviantart.net/eclipse/profile-skins/purple_spots_preview.png?${CACHE_BUSTER})`,
    skin: {
      baseTheme: ThemeOptions.DARK,
      background: `url(https://st.deviantart.net/eclipse/profile-skins/purple_spots.png?${CACHE_BUSTER})`,
    },
  },
  {
    skinId: 'pixelhearts',
    skinName: 'Green Pixel Hearts',
    isCore: true,
    backgroundPreview: `url(https://st.deviantart.net/eclipse/profile-skins/pixel_hearts_green_preview.png?${CACHE_BUSTER})`,
    skin: {
      baseTheme: ThemeOptions.LIGHT,
      background: `url(https://st.deviantart.net/eclipse/profile-skins/pixel_hearts_green.png?${CACHE_BUSTER})`,
    },
  },
  {
    skinId: 'pinkandgold',
    skinName: 'Pink & Gold',
    isCore: true,
    backgroundPreview: `url(https://st.deviantart.net/eclipse/profile-skins/pink_and_gold_preview.png?${CACHE_BUSTER})`,
    skin: {
      baseTheme: ThemeOptions.LIGHT,
      background: `url(https://st.deviantart.net/eclipse/profile-skins/pink_and_gold.png?${CACHE_BUSTER})`,
    },
  },
  {
    skinId: 'orangeflowers',
    skinName: 'Orange Flowers',
    isCore: true,
    backgroundPreview: `url(https://st.deviantart.net/eclipse/profile-skins/orange_flowers_preview.png?${CACHE_BUSTER})`,
    skin: {
      baseTheme: ThemeOptions.LIGHT,
      background: `url(https://st.deviantart.net/eclipse/profile-skins/orange_flowers.png?${CACHE_BUSTER})`,
    },
  },
  {
    skinId: 'leavesbeige',
    skinName: 'Beige Leaves',
    isCore: true,
    backgroundPreview: `url(https://st.deviantart.net/eclipse/profile-skins/leaves_beige_preview.png?${CACHE_BUSTER})`,
    skin: {
      baseTheme: ThemeOptions.LIGHT,
      background: `url(https://st.deviantart.net/eclipse/profile-skins/leaves_beige.png?${CACHE_BUSTER})`,
    },
  },
  {
    skinId: 'greenleaves',
    skinName: 'Green Leaves',
    isCore: true,
    backgroundPreview: `url(https://st.deviantart.net/eclipse/profile-skins/green_leaves_preview.png?${CACHE_BUSTER})`,
    skin: {
      baseTheme: ThemeOptions.DARK,
      background: `url(https://st.deviantart.net/eclipse/profile-skins/green_leaves.png?${CACHE_BUSTER})`,
    },
  },
  {
    skinId: 'circlesblue',
    skinName: 'Blue Circles',
    isCore: true,
    backgroundPreview: `url(https://st.deviantart.net/eclipse/profile-skins/circles_blue_preview.png?${CACHE_BUSTER})`,
    skin: {
      baseTheme: ThemeOptions.LIGHT,
      background: `url(https://st.deviantart.net/eclipse/profile-skins/circles_blue.png?${CACHE_BUSTER})`,
    },
  },
  {
    skinId: 'gradient-tropical',
    skinName: 'Tropical Gradient',
    isCore: true,
    backgroundPreview:
      'linear-gradient(#C6FFDD 0%, #FBD786 51.5%, #F7797D 100%)',
    skin: {
      baseTheme: ThemeOptions.LIGHT,
      background: 'linear-gradient(#C6FFDD 0%, #FBD786 51.5%, #F7797D 100%)',
    },
  },
  {
    skinId: 'gradient-pinkpurple',
    skinName: 'Pink to Purple Gradient',
    isCore: true,
    backgroundPreview: 'linear-gradient(#EECDA3 0%, #EF629F 97.74%)',
    skin: {
      baseTheme: ThemeOptions.LIGHT,
      background: 'linear-gradient(#EECDA3 0%, #EF629F 97.74%)',
    },
  },
  {
    skinId: 'gradient-bluepurple',
    skinName: 'Dark Blue to Purple Gradient',
    isCore: true,
    backgroundPreview: 'linear-gradient(#2D1358 0%, #005276 100%)',
    skin: {
      baseTheme: ThemeOptions.DARK,
      background: 'linear-gradient(#2D1358 0%, #005276 100%)',
    },
  },
  {
    skinId: 'gradient-darkblueblack',
    skinName: 'Dark Blue to Grey Gradient',
    isCore: true,
    backgroundPreview: 'linear-gradient(#364C57 0%, #0D1A23 100%)',
    skin: {
      baseTheme: ThemeOptions.DARK,
      background: 'linear-gradient(#364C57 0%, #0D1A23 100%)',
    },
  },
  {
    skinId: 'solid-D0CFDF',
    skinName: 'Solid Color #D0CFDF',
    isCore: true,
    backgroundPreview: 'linear-gradient(#D0CFDF,#D0CFDF)',
    skin: {
      baseTheme: ThemeOptions.LIGHT,
      background: 'linear-gradient(#D0CFDF,#D0CFDF)',
    },
  },
  {
    skinId: 'solid-#E1D7D2',
    skinName: 'Solid Color #E1D7D2',
    isCore: true,
    backgroundPreview: 'linear-gradient(#E1D7D2,#E1D7D2)',
    skin: {
      baseTheme: ThemeOptions.LIGHT,
      background: 'linear-gradient(#E1D7D2,#E1D7D2)',
    },
  },
  {
    skinId: 'solid-#ECF4FF',
    skinName: 'Solid Color #ECF4FF',
    isCore: true,
    backgroundPreview: 'linear-gradient(#ECF4FF,#ECF4FF)',
    skin: {
      baseTheme: ThemeOptions.LIGHT,
      background: 'linear-gradient(#ECF4FF,#ECF4FF)',
    },
  },
  {
    skinId: 'solid-#F8F8ED',
    skinName: 'Solid Color #F8F8ED',
    isCore: true,
    backgroundPreview: 'linear-gradient(#F8F8ED,#F8F8ED)',
    skin: {
      baseTheme: ThemeOptions.LIGHT,
      background: 'linear-gradient(#F8F8ED,#F8F8ED)',
    },
  },
  {
    skinId: 'solid-#FFECE2',
    skinName: 'Solid Color #FFECE2',
    isCore: true,
    backgroundPreview: 'linear-gradient(#FFECE2,#FFECE2)',
    skin: {
      baseTheme: ThemeOptions.LIGHT,
      background: 'linear-gradient(#FFECE2,#FFECE2)',
    },
  },
  {
    skinId: 'solid-#0D023A',
    skinName: 'Solid Color #0D023A',
    isCore: true,
    backgroundPreview: 'linear-gradient(#0D023A,#0D023A)',
    skin: {
      baseTheme: ThemeOptions.DARK,
      background: 'linear-gradient(#0D023A,#0D023A)',
    },
  },
  {
    skinId: 'solid-#2E2F2E',
    skinName: 'Solid Color #2E2F2E',
    isCore: true,
    backgroundPreview: 'linear-gradient(#2E2F2E,#2E2F2E)',
    skin: {
      baseTheme: ThemeOptions.DARK,
      background: 'linear-gradient(#2E2F2E,#2E2F2E)',
    },
  },
  {
    skinId: 'solid-#021C3A',
    skinName: 'Solid Color #021C3A',
    isCore: true,
    backgroundPreview: 'linear-gradient(#021C3A,#021C3A)',
    skin: {
      baseTheme: ThemeOptions.DARK,
      background: 'linear-gradient(#021C3A,#021C3A)',
    },
  },
  {
    skinId: 'solid-#390E32',
    skinName: 'Solid Color #390E32',
    isCore: true,
    backgroundPreview: 'linear-gradient(#390E32,#390E32)',
    skin: {
      baseTheme: ThemeOptions.DARK,
      background: 'linear-gradient(#390E32,#390E32)',
    },
  },
  {
    skinId: 'solid-#23383B',
    skinName: 'Solid Color #23383B',
    isCore: true,
    backgroundPreview: 'linear-gradient(#23383B,#23383B)',
    skin: {
      baseTheme: ThemeOptions.DARK,
      background: 'linear-gradient(#23383B,#23383B)',
    },
  },
];

export const profileSkins: ProfileSkin[] = [officialSkin, ...premiumSkins];
